// CommunityPageBuilder.js
import React, { useState, useEffect } from 'react';
import {
    ArrowRight, Calendar, MessageSquare, Users, Mail, ChevronDown,
    Type, Image, AlignLeft, Layout, Grid, FileText, Grid3X3, Trash2,
    Move, Edit, Plus, Save, Settings, Eye,
    Loader
} from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useCommunity } from '../../context/CommunityContext';

// Define preset components that will be replaced with real data
const presetComponents = {
    CommunityTitle: {
        type: 'CommunityTitle',
        name: 'Community Title',
        icon: Type,
        defaultProps: {
            text: 'My Community Name',
            size: 'large', // small, medium, large
            alignment: 'center', // left, center, right
        },
    },
    CommunityDescription: {
        type: 'CommunityDescription',
        name: 'Community Description',
        icon: AlignLeft,
        defaultProps: {
            text: 'A short description of your community.',
            size: 'medium', // small, medium, large
        },
    },
    CommunityProfilePicture: {
        type: 'CommunityProfilePicture',
        name: 'Profile Picture',
        icon: Image,
        defaultProps: {
            size: 'medium', // small, medium, large
            shape: 'circle', // circle, square, rounded
        },
    },
    CommunityCoverPicture: {
        type: 'CommunityCoverPicture',
        name: 'Cover Picture',
        icon: Image,
        defaultProps: {
            height: 'medium', // small, medium, large
        },
    },
    PostsView: {
        type: 'PostsView',
        name: 'Posts View',
        icon: FileText,
        defaultProps: {
            layout: 'grid', // list, grid
            postsPerPage: 6,
        },
    },
    StoreItems: {
        type: 'StoreItems',
        name: 'Store Items',
        icon: Grid3X3,
        defaultProps: {
            layout: 'grid', // list, grid
            itemsPerPage: 8,
        },
    },
};

// Generic components that users can add
const genericComponents = {
    Hero: {
        type: 'Hero',
        name: 'Hero Section',
        icon: Layout,
        defaultProps: {
            title: 'Welcome to Our Community',
            subtitle: 'Join us and be part of something amazing.',
            hasButtons: true,
            buttonText1: 'Join Now',
            buttonText2: 'Learn More',
            gradientFrom: 'indigo',
            gradientTo: 'purple',
        },
    },
    Features: {
        type: 'Features',
        name: 'Features Section',
        icon: Grid,
        defaultProps: {
            title: 'What We Offer',
            subtitle: 'Everything you need to grow and succeed',
            features: [
                {
                    title: 'Feature 1',
                    description: 'Description for feature 1',
                    icon: 'Users',
                },
                {
                    title: 'Feature 2',
                    description: 'Description for feature 2',
                    icon: 'MessageSquare',
                },
                {
                    title: 'Feature 3',
                    description: 'Description for feature 3',
                    icon: 'Mail',
                },
            ],
        },
    },
    Events: {
        type: 'Events',
        name: 'Events Section',
        icon: Calendar,
        defaultProps: {
            title: 'Upcoming Events',
            subtitle: 'Join our latest workshops and meetups',
            events: [
                {
                    title: 'Event 1',
                    description: 'Description for event 1',
                    date: 'June 15, 2024',
                    time: '2:00 PM EST',
                    type: 'Workshop',
                },
                {
                    title: 'Event 2',
                    description: 'Description for event 2',
                    date: 'June 22, 2024',
                    time: '3:00 PM EST',
                    type: 'Meetup',
                },
                {
                    title: 'Event 3',
                    description: 'Description for event 3',
                    date: 'June 29, 2024',
                    time: '1:00 PM EST',
                    type: 'Workshop',
                },
            ],
        },
    },
    Text: {
        type: 'Text',
        name: 'Text Section',
        icon: Type,
        defaultProps: {
            title: 'Section Title',
            content: 'Add your content here. This is a simple text section that you can edit.',
            alignment: 'left', // left, center, right
        },
    },
    CTA: {
        type: 'CTA',
        name: 'Call to Action',
        icon: ArrowRight,
        defaultProps: {
            title: 'Ready to Join Our Community?',
            buttonText: 'Get Started Now',
            gradientFrom: 'indigo',
            gradientTo: 'purple',
        },
    },
    FAQ: {
        type: 'FAQ',
        name: 'FAQ Section',
        icon: MessageSquare,
        defaultProps: {
            title: 'Frequently Asked Questions',
            subtitle: 'Find answers to common questions',
            faqs: [
                {
                    question: 'How can I join the community?',
                    answer: 'You can join by clicking the "Join Community" button and following the registration process.',
                },
                {
                    question: 'What benefits do members get?',
                    answer: 'Members get access to exclusive content, events, and more.',
                },
                {
                    question: 'How can I contribute?',
                    answer: 'You can contribute by sharing knowledge, participating in discussions, or becoming a mentor.',
                },
            ],
        },
    },
};

// SortableItem component for drag and drop
const SortableItem = ({ id, component, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const IconComponent = component.icon;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="group bg-white  border border-gray-200  rounded-lg mb-4 overflow-hidden"
        >
            <div className="flex items-center justify-between bg-gray-50  p-3 border-b border-gray-200 ">
                <div className="flex items-center gap-2">
                    <button
                        className="cursor-move p-1 hover:bg-gray-200  rounded"
                        {...attributes}
                        {...listeners}
                    >
                        <Move className="w-5 h-5 text-gray-500 " />
                    </button>
                    <IconComponent className="w-5 h-5 text-indigo-600 " />
                    <span className="font-medium">{component.name}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onEdit(id)}
                        className="p-1 hover:bg-gray-200  rounded"
                    >
                        <Edit className="w-5 h-5 text-gray-500 " />
                    </button>
                    <button
                        onClick={() => onDelete(id)}
                        className="p-1 hover:bg-gray-200  rounded"
                    >
                        <Trash2 className="w-5 h-5 text-red-500" />
                    </button>
                </div>
            </div>
            <div className="p-4 bg-gray-100  text-sm text-gray-500 ">
                {component.type} Component
            </div>
        </div>
    );
};

// Component previews based on type
const ComponentPreview = ({ component }) => {

    const communityCtx = useCommunity();

    switch (component.type) {
        case 'Hero':
            return (
                <section className="relative py-20 border border-dashed border-gray-300  rounded-lg overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br from-${component.props.gradientFrom}-600 to-${component.props.gradientTo}-600`} />
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                        <h1 className="text-4xl sm:text-5xl font-bold mb-6">{component.props.title}</h1>
                        <p className="text-xl text-indigo-100 mb-8 max-w-3xl mx-auto">
                            {component.props.subtitle}
                        </p>
                        {component.props.hasButtons && (
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <button className="px-6 py-3 bg-white text-indigo-600 rounded-lg">
                                    {component.props.buttonText1}
                                </button>
                                <button className="px-6 py-3 bg-indigo-500 text-white rounded-lg">
                                    {component.props.buttonText2}
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            );

        case 'Features':
            return (
                <section className="py-20 bg-white  border border-dashed border-gray-300  rounded-lg">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold">{component.props.title}</h2>
                            <p className="mt-4 text-lg text-gray-600 ">
                                {component.props.subtitle}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {component.props.features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="relative group bg-gray-50  p-8 rounded-xl"
                                >
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl opacity-0 group-hover:opacity-100" />
                                    <div className="relative bg-gray-50  p-8 rounded-xl">
                                        {/* This is a placeholder - in real app, map icon name to actual component */}
                                        <Users className="w-12 h-12 text-indigo-600  mb-4" />
                                        <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                        <p className="text-gray-600 ">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            );

        case 'CommunityTitle':
            return (
                <div className={`text-${component.props.alignment} py-4 border border-dashed border-gray-300  rounded-lg`}>
                    <h1 className={`text-${component.props.size === 'small' ? '2xl' : component.props.size === 'medium' ? '3xl' : '4xl'} font-bold`}>
                        {communityCtx.community.community.name}
                    </h1>
                </div>
            );

        case 'CommunityDescription':
            return (
                <div className="py-4 border border-dashed border-gray-300  rounded-lg">
                    <p className={`text-${component.props.size === 'small' ? 'base' : component.props.size === 'medium' ? 'lg' : 'xl'} text-gray-600`}>
                        {communityCtx.community.community.description}
                    </p>
                </div>
            );

        case 'CommunityProfilePicture':
            return (
                <div className="flex justify-center py-4 border border-dashed border-gray-300  rounded-lg">
                    <div className={`
            bg-gray-200  flex items-center justify-center text-gray-400
            ${component.props.size === 'small' ? 'w-24 h-24' : component.props.size === 'medium' ? 'w-32 h-32' : 'w-40 h-40'}
            ${component.props.shape === 'circle' ? 'rounded-full' : component.props.shape === 'rounded' ? 'rounded-lg' : ''}
          `}>
                        <Image className="w-12 h-12" src={communityCtx.community.community.profilePicture} alt="Profile Picture" />
                        <span className="sr-only">Profile Picture</span>
                        <span className="text-sm absolute mt-20">Will be replaced with actual profile picture</span>
                    </div>
                </div>
            );

        case 'CommunityCoverPicture':
            return (
                <div className="border border-dashed border-gray-300  rounded-lg overflow-hidden">
                    <div className={`
            bg-gray-200  w-full flex items-center justify-center text-gray-400
            ${component.props.height === 'small' ? 'h-32' : component.props.height === 'medium' ? 'h-48' : 'h-64'}
          `}>
                        <Image className="w-12 h-12" src={communityCtx.community.community.coverPicture} alt="Cover Picture" />
                        <span className="sr-only">Cover Picture</span>
                        <span className="text-sm">Will be replaced with actual cover picture</span>
                    </div>
                </div>
            );

        case 'PostsView':
            return (
                <div className="py-6 border border-dashed border-gray-300  rounded-lg">
                    <h3 className="text-xl font-bold mb-4">Community Posts</h3>
                    <div className={`grid ${component.props.layout === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
                        {[...Array(Math.min(component.props.postsPerPage, 3))].map((_, i) => (
                            <div key={i} className="bg-white  p-4 rounded-lg shadow">
                                <div className="bg-gray-200  w-full h-32 rounded mb-3"></div>
                                <h4 className="font-bold">Post Title {i + 1}</h4>
                                <p className="text-sm text-gray-600 ">This is a sample post. Will be replaced with actual posts.</p>
                            </div>
                        ))}
                    </div>
                </div>
            );

        case 'StoreItems':
            return (
                <div className="py-6 border border-dashed border-gray-300  rounded-lg">
                    <h3 className="text-xl font-bold mb-4">Store Items</h3>
                    <div className={`grid ${component.props.layout === 'grid' ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-4`}>
                        {[...Array(Math.min(component.props.itemsPerPage, 4))].map((_, i) => (
                            <div key={i} className="bg-white  p-3 rounded-lg shadow">
                                <div className="bg-gray-200  w-full h-24 rounded mb-2"></div>
                                <h4 className="font-bold text-sm">Product {i + 1}</h4>
                                <p className="text-xs text-gray-600 ">$19.99</p>
                                <span className="text-xs italic">(Will be replaced with actual store items)</span>
                            </div>
                        ))}
                    </div>
                </div>
            );

        case 'Events':
            return (
                <section className="py-20 border border-dashed border-gray-300  rounded-lg">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between mb-12">
                            <div>
                                <h2 className="text-3xl font-bold">{component.props.title}</h2>
                                <p className="mt-2 text-gray-600 ">{component.props.subtitle}</p>
                            </div>
                            <button className="flex items-center gap-2 text-indigo-600 ">
                                View All Events
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {component.props.events.map((event, index) => (
                                <div
                                    key={index}
                                    className="bg-white  rounded-xl overflow-hidden shadow"
                                >
                                    <div className="aspect-video relative bg-gray-200 ">
                                        <div className="absolute top-4 left-4 px-3 py-1 bg-indigo-600 text-white text-sm rounded-full">
                                            {event.type}
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                                            <Calendar className="w-4 h-4" />
                                            {event.date} • {event.time}
                                        </div>
                                        <h3 className="text-xl font-bold mb-2">{event.title}</h3>
                                        <p className="text-gray-600  mb-4">
                                            {event.description}
                                        </p>
                                        <button className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg">
                                            Register Now
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            );

        case 'Text':
            return (
                <section className="py-10 border border-dashed border-gray-300  rounded-lg">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className={`text-${component.props.alignment}`}>
                            <h2 className="text-3xl font-bold mb-6">{component.props.title}</h2>
                            <div className="prose  max-w-none text-gray-600 ">
                                <p>{component.props.content}</p>
                            </div>
                        </div>
                    </div>
                </section>
            );

        case 'FAQ':
            return (
                <section className="py-20 border border-dashed border-gray-300  rounded-lg">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold">{component.props.title}</h2>
                            <p className="mt-2 text-gray-600 ">{component.props.subtitle}</p>
                        </div>

                        <div className="space-y-4">
                            {component.props.faqs.map((faq, index) => (
                                <div key={index} className="bg-white  rounded-xl p-6 shadow">
                                    <button className="flex items-center justify-between w-full text-left">
                                        <span className="font-semibold">{faq.question}</span>
                                        <ChevronDown className="w-5 h-5 text-gray-500" />
                                    </button>
                                    <p className="mt-4 text-gray-600 ">{faq.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            );

        case 'CTA':
            return (
                <section className={`py-20 bg-gradient-to-r from-${component.props.gradientFrom}-600 to-${component.props.gradientTo}-600 border border-dashed border-gray-300  rounded-lg`}>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                        <h2 className="text-3xl font-bold mb-8">{component.props.title}</h2>
                        <button className="px-8 py-3 bg-white text-indigo-600 rounded-lg">
                            {component.props.buttonText}
                        </button>
                    </div>
                </section>
            );

        default:
            return (
                <div className="p-4 border border-dashed border-gray-300  rounded-lg">
                    <p>Unknown component type: {component.type}</p>
                </div>
            );
    }
};

// Component Editor Modal
const ComponentEditorModal = ({ component, onSave, onCancel }) => {
    const [formState, setFormState] = useState(component.props);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormState({
            ...formState,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleArrayChange = (index, field, value, arrayName) => {
        const newArray = [...formState[arrayName]];
        newArray[index] = { ...newArray[index], [field]: value };
        setFormState({ ...formState, [arrayName]: newArray });
    };

    const renderFormFields = () => {
        switch (component.type) {
            case 'Hero':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formState.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Subtitle</label>
                            <input
                                type="text"
                                name="subtitle"
                                value={formState.subtitle}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="hasButtons"
                                    checked={formState.hasButtons}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                />
                                <span>Show Buttons</span>
                            </label>
                        </div>
                        {formState.hasButtons && (
                            <>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Button 1 Text</label>
                                    <input
                                        type="text"
                                        name="buttonText1"
                                        value={formState.buttonText1}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Button 2 Text</label>
                                    <input
                                        type="text"
                                        name="buttonText2"
                                        value={formState.buttonText2}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Gradient From</label>
                                <select
                                    name="gradientFrom"
                                    value={formState.gradientFrom}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="indigo">Indigo</option>
                                    <option value="blue">Blue</option>
                                    <option value="purple">Purple</option>
                                    <option value="pink">Pink</option>
                                    <option value="red">Red</option>
                                    <option value="orange">Orange</option>
                                    <option value="green">Green</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Gradient To</label>
                                <select
                                    name="gradientTo"
                                    value={formState.gradientTo}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="purple">Purple</option>
                                    <option value="indigo">Indigo</option>
                                    <option value="blue">Blue</option>
                                    <option value="pink">Pink</option>
                                    <option value="red">Red</option>
                                    <option value="orange">Orange</option>
                                    <option value="green">Green</option>
                                </select>
                            </div>
                        </div>
                    </>
                );

            case 'Features':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formState.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Subtitle</label>
                            <input
                                type="text"
                                name="subtitle"
                                value={formState.subtitle}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <h3 className="font-medium mb-2 mt-4">Features</h3>
                        {formState.features.map((feature, index) => (
                            <div key={index} className="p-3 mb-3 border border-gray-200 rounded-md">
                                <div className="mb-2">
                                    <label className="block text-sm font-medium mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={feature.title}
                                        onChange={(e) => handleArrayChange(index, 'title', e.target.value, 'features')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={feature.description}
                                        onChange={(e) => handleArrayChange(index, 'description', e.target.value, 'features')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        ))}
                    </>
                );

            case 'CommunityTitle':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title Text (Preview Only)</label>
                            <input
                                type="text"
                                name="text"
                                value={formState.text}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                            <p className="text-sm text-gray-500 mt-1">This will be replaced with actual community title</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Size</label>
                            <select
                                name="size"
                                value={formState.size}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Alignment</label>
                            <select
                                name="alignment"
                                value={formState.alignment}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </div>
                    </>
                );

            case 'CommunityDescription':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Description Text (Preview Only)</label>
                            <textarea
                                name="text"
                                value={formState.text}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows="3"
                            ></textarea>
                            <p className="text-sm text-gray-500 mt-1">This will be replaced with actual community description</p>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Size</label>
                            <select
                                name="size"
                                value={formState.size}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </select>
                        </div>
                    </>
                );

            case 'CommunityProfilePicture':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Size</label>
                            <select
                                name="size"
                                value={formState.size}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Shape</label>
                            <select
                                name="shape"
                                value={formState.shape}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="circle">Circle</option>
                                <option value="square">Square</option>
                                <option value="rounded">Rounded</option>
                            </select>
                        </div>
                    </>
                );

            case 'CommunityCoverPicture':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Height</label>
                            <select
                                name="height"
                                value={formState.height}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </select>
                        </div>
                    </>
                );

            case 'PostsView':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Layout</label>
                            <select
                                name="layout"
                                value={formState.layout}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="grid">Grid</option>
                                <option value="list">List</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Posts Per Page</label>
                            <input
                                type="number"
                                name="postsPerPage"
                                value={formState.postsPerPage}
                                onChange={handleInputChange}
                                min="1"
                                max="12"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </>
                );

            case 'StoreItems':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Layout</label>
                            <select
                                name="layout"
                                value={formState.layout}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="grid">Grid</option>
                                <option value="list">List</option>
                            </select>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Items Per Page</label>
                            <input
                                type="number"
                                name="itemsPerPage"
                                value={formState.itemsPerPage}
                                onChange={handleInputChange}
                                min="1"
                                max="12"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                    </>
                );

            case 'Events':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formState.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Subtitle</label>
                            <input
                                type="text"
                                name="subtitle"
                                value={formState.subtitle}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <h3 className="font-medium mb-2 mt-4">Events</h3>
                        {formState.events.map((event, index) => (
                            <div key={index} className="p-3 mb-3 border border-gray-200 rounded-md">
                                <div className="mb-2">
                                    <label className="block text-sm font-medium mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={event.title}
                                        onChange={(e) => handleArrayChange(index, 'title', e.target.value, 'events')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block text-sm font-medium mb-1">Description</label>
                                    <input
                                        type="text"
                                        value={event.description}
                                        onChange={(e) => handleArrayChange(index, 'description', e.target.value, 'events')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Date</label>
                                        <input
                                            type="text"
                                            value={event.date}
                                            onChange={(e) => handleArrayChange(index, 'date', e.target.value, 'events')}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Time</label>
                                        <input
                                            type="text"
                                            value={event.time}
                                            onChange={(e) => handleArrayChange(index, 'time', e.target.value, 'events')}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <label className="block text-sm font-medium mb-1">Event Type</label>
                                    <input
                                        type="text"
                                        value={event.type}
                                        onChange={(e) => handleArrayChange(index, 'type', e.target.value, 'events')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        ))}
                    </>
                );

            case 'Text':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formState.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Content</label>
                            <textarea
                                name="content"
                                value={formState.content}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows="5"
                            ></textarea>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Alignment</label>
                            <select
                                name="alignment"
                                value={formState.alignment}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </div>
                    </>
                );

            case 'FAQ':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formState.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Subtitle</label>
                            <input
                                type="text"
                                name="subtitle"
                                value={formState.subtitle}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <h3 className="font-medium mb-2 mt-4">FAQs</h3>
                        {formState.faqs.map((faq, index) => (
                            <div key={index} className="p-3 mb-3 border border-gray-200 rounded-md">
                                <div className="mb-2">
                                    <label className="block text-sm font-medium mb-1">Question</label>
                                    <input
                                        type="text"
                                        value={faq.question}
                                        onChange={(e) => handleArrayChange(index, 'question', e.target.value, 'faqs')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Answer</label>
                                    <textarea
                                        value={faq.answer}
                                        onChange={(e) => handleArrayChange(index, 'answer', e.target.value, 'faqs')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>
                        ))}
                    </>
                );

            case 'CTA':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formState.title}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-1">Button Text</label>
                            <input
                                type="text"
                                name="buttonText"
                                value={formState.buttonText}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Gradient From</label>
                                <select
                                    name="gradientFrom"
                                    value={formState.gradientFrom}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="indigo">Indigo</option>
                                    <option value="blue">Blue</option>
                                    <option value="purple">Purple</option>
                                    <option value="pink">Pink</option>
                                    <option value="red">Red</option>
                                    <option value="orange">Orange</option>
                                    <option value="green">Green</option>
                                </select>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">Gradient To</label>
                                <select
                                    name="gradientTo"
                                    value={formState.gradientTo}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="purple">Purple</option>
                                    <option value="indigo">Indigo</option>
                                    <option value="blue">Blue</option>
                                    <option value="pink">Pink</option>
                                    <option value="red">Red</option>
                                    <option value="orange">Orange</option>
                                    <option value="green">Green</option>
                                </select>
                            </div>
                        </div>
                    </>
                );

            default:
                return <p>No editor available for this component type.</p>;
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white  rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b border-gray-200  flex justify-between items-center">
                    <h3 className="text-xl font-bold">Edit {component.name}</h3>
                    <button
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700  "
                    >
                        &times;
                    </button>
                </div>
                <div className="p-4">
                    {renderFormFields()}
                </div>
                <div className="p-4 border-t border-gray-200  flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300  rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSave(formState)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Component
const CommunityPageBuilder = () => {
    const [components, setComponents] = useState([]);
    const [editingComponent, setEditingComponent] = useState(null);
    const [isAddingComponent, setIsAddingComponent] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [pageName, setPageName] = useState("Homepage");
    const communityCtx = useCommunity();

    const [saveButtonState, setSaveButtonState] = useState("idle");

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor)
    );

    useEffect(() => {
        if (saveButtonState === "success" || saveButtonState === "error") {
            setTimeout(() => {
                setSaveButtonState("idle");
            }, 3000);
        }
    }, [saveButtonState]);

    useEffect(() => {
        if (communityCtx.page) {
            let kk = JSON.parse(communityCtx.page.pageCode);
            let finalC = [];
            kk.components.map((k) => {
                finalC.push(addComponentFull(k.type, k.id, k.props));
            })
            setComponents(finalC);
        }
        else {
            setComponents([]);
        }
    }, [communityCtx.page]);

    // Generate a unique ID for components
    const generateId = () => `component-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    // Add a component to the page
    const addComponent = (componentType, componentCategory) => {
        const componentInfo = componentCategory === 'preset'
            ? presetComponents[componentType]
            : genericComponents[componentType];

        if (!componentInfo) return;

        const newComponent = {
            id: generateId(),
            type: componentInfo.type,
            name: componentInfo.name,
            icon: componentInfo.icon,
            props: { ...componentInfo.defaultProps },
        };

        setComponents([...components, newComponent]);
        setIsAddingComponent(false);
    };


    // Add a component to the page
    const addComponentFull = (componentType, componentId, componentProps) => {

        const componentInfoX = presetComponents[componentType] || genericComponents[componentType];

        console.log(componentInfoX, componentType);

        const newComponent = {
            id: componentId,
            type: componentType,
            name: componentInfoX.name,
            icon: componentInfoX.icon,
            props: { ...componentProps },
        };

        return newComponent;

        // setComponents([...components, newComponent]);
        // setIsAddingComponent(false);
    };

    // Edit a component
    const editComponent = (id) => {
        const component = components.find(c => c.id === id);
        if (component) {
            setEditingComponent(component);
        }
    };

    // Save edited component
    const saveComponent = (updatedProps) => {
        setComponents(components.map(component =>
            component.id === editingComponent.id
                ? { ...component, props: updatedProps }
                : component
        ));
        setEditingComponent(null);
    };

    // Delete a component
    const deleteComponent = (id) => {
        setComponents(components.filter(component => component.id !== id));
    };

    // Handle drag end event
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const activeIndex = components.findIndex(c => c.id === active.id);
            const overIndex = components.findIndex(c => c.id === over.id);

            const newComponents = [...components];
            const [removed] = newComponents.splice(activeIndex, 1);
            newComponents.splice(overIndex, 0, removed);

            setComponents(newComponents);
        }
    };

    // Generate JSON for saving to the database
    const generatePageJSON = () => {
        console.log("C : ", components)
        return JSON.stringify({
            pageName,
            components: components.map(({ id, type, props }) => ({ id, type, props })),
        }, null, 2);
    };

    const handleSavePage = () => {
        setSaveButtonState("loading");
        const code = generatePageJSON();
        communityCtx.updatePage({
            pageCode: code
        }).then(() => {
            setSaveButtonState("success");
        }).catch(() => {
            window.alert("Failed to save page");
            setSaveButtonState("error");
        });
    }

    // Add Component Modal
    const AddComponentModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white  rounded-lg max-w-2xl w-full">
                <div className="p-4 border-b border-gray-200  flex justify-between items-center">
                    <h3 className="text-xl font-bold">Add Component</h3>
                    <button
                        onClick={() => setIsAddingComponent(false)}
                        className="text-gray-500 hover:text-gray-700  "
                    >
                        &times;
                    </button>
                </div>
                <div className="p-4">
                    <h4 className="font-medium mb-3 text-indigo-600 ">Preset Components</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                        {Object.entries(presetComponents).map(([key, component]) => {
                            const Icon = component.icon;
                            return (
                                <button
                                    key={key}
                                    onClick={() => addComponent(key, 'preset')}
                                    className="flex flex-col items-center justify-center p-4 border border-gray-200  rounded-lg hover:bg-gray-50 "
                                >
                                    <Icon className="w-8 h-8 text-indigo-600  mb-2" />
                                    <span className="text-sm text-center">{component.name}</span>
                                </button>
                            );
                        })}
                    </div>

                    <h4 className="font-medium mb-3 text-indigo-600 ">Generic Components</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {Object.entries(genericComponents).map(([key, component]) => {
                            const Icon = component.icon;
                            return (
                                <button
                                    key={key}
                                    onClick={() => addComponent(key, 'generic')}
                                    className="flex flex-col items-center justify-center p-4 border border-gray-200  rounded-lg hover:bg-gray-50 "
                                >
                                    <Icon className="w-8 h-8 text-indigo-600  mb-2" />
                                    <span className="text-sm text-center">{component.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="p-4 border-t border-gray-200  text-right">
                    <button
                        onClick={() => setIsAddingComponent(false)}
                        className="px-4 py-2 border border-gray-300  rounded-lg"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100 ">
            {/* Header */}
            <header className="bg-white  shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <input
                        type="text"
                        value={pageName}
                        onChange={(e) => setPageName(e.target.value)}
                        className="text-xl font-bold bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded px-2 py-1"
                    />
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setPreviewMode(!previewMode)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg ${previewMode
                                ? 'bg-indigo-100 text-indigo-700  '
                                : 'bg-gray-100 text-gray-700  '
                                }`}
                        >
                            <Eye className="w-4 h-4" />
                            {previewMode ? 'Exit Preview' : 'Preview'}
                        </button>
                        <button
                            onClick={() => {
                                // This is where you'd normally save to the database
                                // alert("Page configuration saved:\n\n" + generatePageJSON());
                                handleSavePage();
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                        >
                            {saveButtonState === "idle" ? (
                                <>
                                    <Save className="w-4 h-4" />
                                    Save Page
                                </>
                            ) : saveButtonState === "loading" ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : saveButtonState === "success" ? (
                                <>
                                    <Save className="w-4 h-4" />
                                    Saved!
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 text-red-500" />
                                    Failed to save
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {previewMode ? (
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="bg-white  overflow-hidden shadow-sm rounded-lg">
                        {components.length === 0 ? (
                            <div className="p-20 text-center text-gray-500 ">
                                <p className="text-lg">No components added yet. Exit preview mode to start building.</p>
                            </div>
                        ) : (
                            <div>
                                {components.map((component) => (
                                    <ComponentPreview key={component.id} component={component} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row gap-6">
                        {/* Builder Panel */}
                        <div className="w-full md:w-2/3 bg-white  p-6 rounded-lg shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-medium">Page Components</h2>
                                <button
                                    onClick={() => setIsAddingComponent(true)}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Component
                                </button>
                            </div>

                            {components.length === 0 ? (
                                <div className="border-2 border-dashed border-gray-300  rounded-lg p-8 text-center">
                                    <p className="text-gray-500  mb-4">
                                        Your page is empty. Add components to get started.
                                    </p>
                                    <button
                                        onClick={() => setIsAddingComponent(true)}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                    >
                                        Add First Component
                                    </button>
                                </div>
                            ) : (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                    modifiers={[restrictToVerticalAxis]}
                                >
                                    <SortableContext items={components.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                        {components.map((component) => (
                                            <SortableItem
                                                key={component.id}
                                                id={component.id}
                                                component={component}
                                                onEdit={editComponent}
                                                onDelete={deleteComponent}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            )}
                        </div>

                        {/* Help Panel */}
                        <div className="w-full md:w-1/3 bg-white  p-6 rounded-lg shadow-sm">
                            <div className="mb-6">
                                <h3 className="text-lg font-medium mb-2">How to Build Your Page</h3>
                                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 ">
                                    <li>Add components using the "Add Component" button</li>
                                    <li>Drag and drop components to reorder them</li>
                                    <li>Click the edit icon to customize any component</li>
                                    <li>Use the preview mode to see how your page will look</li>
                                    <li>Save your page when you're satisfied with the result</li>
                                </ol>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium mb-2">Special Components</h3>
                                <p className="text-sm text-gray-600  mb-4">
                                    These components will be dynamically replaced with actual data when your page is displayed:
                                </p>
                                <ul className="space-y-2 text-sm text-gray-600 ">
                                    <li><strong>CommunityTitle:</strong> Shows your community name</li>
                                    <li><strong>CommunityDescription:</strong> Shows your community description</li>
                                    <li><strong>CommunityProfilePicture:</strong> Shows your community profile picture</li>
                                    <li><strong>CommunityCoverPicture:</strong> Shows your community cover image</li>
                                    <li><strong>PostsView:</strong> Shows posts from your community</li>
                                    <li><strong>StoreItems:</strong> Shows items from your community store</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isAddingComponent && <AddComponentModal />}
            {editingComponent && (
                <ComponentEditorModal
                    component={editingComponent}
                    onSave={saveComponent}
                    onCancel={() => setEditingComponent(null)}
                />
            )}
        </div>
    );
};

export default CommunityPageBuilder;